import { useEffect, useState } from 'react'

/**
 * Hook to detect if an element's content is overflowing horizontally
 * @param elementRef - Ref to the element to monitor
 * @param deps - Optional dependency array to re-check overflow when dependencies change
 * @returns boolean indicating if content is overflowing
 */
export function useIsOverflowing(elementRef: React.RefObject<HTMLElement | null>, deps?: React.DependencyList) {
  // Force a second render to ensure that there's always at least one render with the ref set.
  const [isFirstRender, setIsFirstRender] = useState(true)
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false)
    }
  }, [isFirstRender])

  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    const checkOverflow = () => {
      const isOverflowingHorizontally = element.scrollWidth > element.clientWidth
      setIsOverflowing(isOverflowingHorizontally)
    }

    // Check initially
    checkOverflow()

    // Create a ResizeObserver to monitor size changes
    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(element)

    // Also listen for content changes that might affect overflow
    const mutationObserver = new MutationObserver(checkOverflow)
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [elementRef.current, ...(deps || [])])

  return isOverflowing
}
